import { useEffect, useState } from 'react';
import type { SafetyAlertLike } from '@/components/SafetyAlertBanner';
import { checkDispensingSafety, type DispenseMismatch } from './api';

/**
 * The debounced, advisory, pre-checkout call to POST /pharmacy/check that
 * both PharmacyStage.tsx and DispensingTab.tsx make against the cart while
 * it's being built — identical network call and debounce, shared here.
 * ADVISORY ONLY: neither screen wires this into its "generate bill"
 * disabled prop, matching SafetyAlertBanner's own stated rule. A failed
 * check is silent, not a blocker.
 *
 * `depsKey` is the caller's own re-run trigger, passed through rather than
 * computed here, because the two screens genuinely differ on when a new
 * network call is worth making: DispensingTab keys on substitution_reason
 * too (a resolved substitution should stop the mismatch it just explained
 * from showing as unresolved — see that file's own unresolvedMismatches),
 * PharmacyStage doesn't need to since its cart can never carry a
 * substitution_reason in the first place. Passing the key through preserves
 * each screen's existing behaviour exactly rather than picking one.
 */
export function useDispensingSafetyCheck(
  patientId: number | null | undefined,
  items: { inventory_id: number; prescription_item_id?: number }[],
  depsKey: string
): { alerts: SafetyAlertLike[]; mismatches: DispenseMismatch[] } {
  const [alerts, setAlerts] = useState<SafetyAlertLike[]>([]);
  const [mismatches, setMismatches] = useState<DispenseMismatch[]>([]);

  useEffect(() => {
    if (!patientId || items.length === 0) {
      setAlerts([]);
      setMismatches([]);
      return;
    }
    const handle = setTimeout(() => {
      checkDispensingSafety(
        patientId,
        items.map((i) => ({ inventory_id: i.inventory_id, prescription_item_id: i.prescription_item_id }))
      )
        .then((r) => {
          setAlerts(r.alerts);
          setMismatches(r.mismatches);
        })
        .catch(() => {
          /* advisory only — a failed check is silent, not a blocker */
        });
    }, 500);
    return () => clearTimeout(handle);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [patientId, depsKey]);

  return { alerts, mismatches };
}
